import React, { useState, useEffect } from 'react';
import documentRequirementsService from '../../../../services/documentRequirementsService';
import LoadingState from '../../../LoadingState';

/**
 * Step 4: PDF Generation & Status Update
 * 
 * Purpose: Generate PDF from filled template, update document status
 * 
 * Process:
 * 1. Submit form data + template ID to backend
 * 2. Backend: merge template with form data, generate PDF, update status
 * 3. Handle errors with admin contact info
 * 4. Allow retry or cancel
 */
const Step4_PdfGeneration = ({
  wizardData,
  template,
  onSuccess,
  onError,
  onDataChange
}) => {
  const [status, setStatus] = useState('ready'); // ready, generating, success, error
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [generatedPdf, setGeneratedPdf] = useState(null);

  // Auto-start PDF generation when component mounts
  // Only generate if we haven't already generated successfully (check wizardData for existing PDF)
  useEffect(() => {
    if (!wizardData.pdfUrl && status === 'ready' && !generatedPdf) {
      generatePdf();
    } else if (wizardData.pdfUrl) {
      // Already have PDF from previous generation, show success state
      setStatus('success');
      setMessage('PDF previously generated');
    }
  }, []);

  const generatePdf = async () => {
    try {
      setStatus('generating');
      setMessage('Generating PDF... Please wait.');
      setErrorMessage('');

      // Extract form data - signature field will be included if present in form
      let formData = wizardData.formData || {};
      let signatureData = null;

      // If signature was provided as a form field, extract it to top-level signature_data
      // Signature requirement is determined by form template, not by acceptance mode
      if (formData.signature) {
        signatureData = formData.signature;
        // Remove signature from form_data (to avoid duplication)
        const { signature, ...restFormData } = formData;
        formData = restFormData;
      }

      // Prepare submission payload
      // Note: signature_data can be null (for acknowledgment-only documents)
      // or contain signature data (if user provided signature in the form)
      const payload = {
        signature_data: signatureData,
        acknowledged: wizardData.acknowledged,
        signer_name: wizardData.participantName || 'User',
        form_data: formData
      };

      console.log('[PDF Generation] Submitting:', payload);
      console.log('[PDF Generation] Wizard data:', wizardData);

      // Get assignment ID from wizard data
      // documentId is set from the assignment ID passed into the wizard from DocumentCompletion page
      const assignmentId = wizardData.documentId;

      console.log('[PDF Generation] Assignment ID:', assignmentId);

      if (!assignmentId) {
        throw new Error('Assignment ID not found. Cannot complete document. Please refresh and try again.');
      }

      // Call backend to generate PDF using document requirements service
      const data = await documentRequirementsService.completeAssignment(assignmentId, payload);
      
      setGeneratedPdf({
        pdfUrl: data.pdf_url,
        documentId: data.document_id,
        completed_at: new Date().toISOString(),
        status: 'completed'
      });

      setStatus('success');
      setMessage('PDF generated successfully!');
      
      onDataChange({
        ...wizardData,
        pdfUrl: data.pdf_url,
        documentId: data.document_id,
        completed_at: new Date().toISOString()
      });

      onSuccess(data);
    } catch (err) {
      console.error('Error generating PDF:', err);
      
      setStatus('error');
      setErrorMessage(err.message || 'Failed to generate PDF');
      
      onError({
        error: err.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Indicator */}
      {status === 'generating' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          padding: '2rem'
        }}>
          <LoadingState
            variant="inline"
            size="large"
            message="Generating your PDF document... Please wait."
            spinnerColor="primary"
          />
        </div>
      )}

      {status === 'success' && (
        <div className="rounded-lg p-6 border-2 bg-green-50 border-green-200">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 text-2xl">✓</div>
            <div>
              <h3 className="font-semibold text-green-900">Document Generated Successfully</h3>
              <p className="text-sm text-green-800 mt-1">{message}</p>
            </div>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-lg p-6 border-2 bg-red-50 border-red-200">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 text-2xl">✕</div>
            <div>
              <h3 className="font-semibold text-red-900">Error Generating Document</h3>
              <p className="text-sm text-red-800 mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Details and Support */}
      {status === 'error' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-900 mb-2">What to do next:</h4>
          <ul className="text-sm text-yellow-800 space-y-2">
            <li>• Try clicking the "Retry" button below to generate the PDF again</li>
            <li>• Review your form entries to ensure all information is correct</li>
            <li>• If the error persists, contact support for assistance</li>
          </ul>
        </div>
      )}

      {/* Technical Error Details (for debugging) */}
      {status === 'error' && (
        <details className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <summary className="cursor-pointer font-semibold text-gray-700">Technical Details</summary>
          <pre className="mt-2 text-xs text-gray-600 overflow-auto bg-white p-2 rounded border border-gray-200">
{`Error: ${errorMessage}
Timestamp: ${new Date().toISOString()}
Template ID: ${wizardData.templateId}`}
          </pre>
        </details>
      )}

      {/* Success Message with Next Steps */}
      {status === 'success' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">What happens next:</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>✓ Your document PDF has been generated and stored securely</li>
            <li>✓ Click "Next" to view and download your completed document</li>
            <li>✓ You'll have access to download it from your document list</li>
          </ul>
        </div>
      )}

      {/* Document Information */}
      {generatedPdf && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Document Details</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Document ID:</dt>
              <dd className="font-mono text-gray-900">{generatedPdf.documentId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Completed At:</dt>
              <dd className="text-gray-900">{new Date(generatedPdf.completed_at).toLocaleString()}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Status:</dt>
              <dd className="text-green-600 font-semibold">{generatedPdf.status}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Action Buttons */}
      {status === 'error' && (
        <button
          onClick={generatePdf}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Retry
        </button>
      )}
    </div>
  );
};

export { Step4_PdfGeneration };
